/* The decide card's two candidates, SIDE BY SIDE (Tier-2 D1).
 *
 * The B6 audit's finding on the live decide card: "the card gives
 * nothing to decide with — no passage, no issued figure, no date for
 * either candidate". The officer was asked whether two objects are the
 * same while looking at two names.
 *
 * This block puts what the payload holds for each side next to the
 * other: the document's wording where a source chip carries an excerpt,
 * and what each side states. It never invents the missing half — a side
 * with no quoted wording SAYS it has none and shows its figures
 * instead. Everything here is read out of the deck payload; nothing is
 * fetched and nothing is derived.
 *
 * Mock of record: ux-audit-2026-09-04/mocks/rk-stream-list-detail.png.
 */

import type { CandidateSide } from './decideSides'
import type { ClaimChip } from './types'

/** No passage on this side — say which kind of silence it is. */
function noPassageLine(side: CandidateSide): string {
  if (side.state === 'on_record')
    return 'On the record already — no document wording is quoted for it here.'
  return 'No excerpt on this source — open the document to read the wording.'
}

export default function RkCandidates({
  sides,
  onOpenChip,
}: {
  sides: CandidateSide[]
  onOpenChip?: (chip: ClaimChip, side: CandidateSide) => void
}) {
  if (sides.length < 2) return null
  return (
    <div className="rkcands" data-testid="decide-candidates">
      {sides.map((s, i) => (
        <div
          key={`${s.name}-${i}`}
          className="rkcand"
          data-testid="decide-candidate"
          data-state={s.state}
        >
          <div className="ch">
            <span className="nm">{s.name}</span>
            <span className="r">
              {s.chip ? (
                <button
                  type="button"
                  className="rkc-chip"
                  data-testid="decide-candidate-chip"
                  onClick={() => onOpenChip?.(s.chip as ClaimChip, s)}
                >
                  {s.chip.label} 📄
                </button>
              ) : null}
              <span className={`rkc-st ${s.state === 'new' ? 'st-new' : 'st-rec'}`}>
                {s.state === 'new' ? 'NEW' : 'ON RECORD'}
              </span>
            </span>
          </div>
          {s.chip?.snippet ? (
            <div className="quote" data-testid="decide-candidate-passage">
              …{s.chip.snippet}…
            </div>
          ) : (
            <div className="nopassage" data-testid="decide-candidate-nopassage">
              {noPassageLine(s)}
            </div>
          )}
          {s.stated ? (
            <div className="stated" data-testid="decide-candidate-stated">
              {s.stated}
            </div>
          ) : null}
          {s.figures.length ? (
            <dl className="kv" data-testid="decide-candidate-figures">
              {s.figures.map(([k, v]) => (
                <div key={k} className="kvrow">
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          ) : s.stated ? null : (
            <div className="nofigures" data-testid="decide-candidate-nofigures">
              Nothing further stated on this side.
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
